import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import PropTypes from 'prop-types';
import AvatarLogo from '../../general/AvatarLogo';
import { Button } from '@material-tailwind/react';
import { useSasToken, useUploadImage } from '../../../datahooks/admin/imageUploadHooks';
import AlertManager from '../../general/AlertManager';
import { FaImage } from 'react-icons/fa';

const ImageUploader = forwardRef(({ imgSrc, onSavePath, imageType, imageId, onImageSelect, displayType }, ref) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(imgSrc || '');

    const fileInputRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    
    const { sasToken, isLoading: isTokenLoading, error: isTokenError, triggerFetchToken } = useSasToken('upload');
    const { uploadAsync, isLoading: isUploading, uploadProgress, allowedTypes } = useUploadImage(onSavePath);
    const pendingSASToken = useRef(false); // useRef updates immediately without re-render and persists across renders
    const lastUsedToken = useRef(null);

    useImperativeHandle(ref, () => ({
        uploadImage: async () => {
 
            if (!selectedFile) {
                setErrorMessage('No file selected for upload');
                return;
            }

            if (isTokenError) {
                setErrorMessage('Error fetching SAS token');
                return;
            }

            if (!sasToken && !isTokenLoading) {
                pendingSASToken.current = true;
                triggerFetchToken();
                return;
            }

            try {
                const filename = await uploadAsync({
                    file: selectedFile,
                    sasToken,
                    type: imageType,
                    id: imageId
                });

                const filenameWithTimestamp = `${filename}?${Date.now()}`;
                onSavePath(filenameWithTimestamp);
            } catch (uploadError) {
                setErrorMessage(`Error uploading image: ${uploadError.message}`);
            }
        },
        resetImage: (clearAll = false) => {
            setSelectedFile(null);
            setPreviewUrl(clearAll ? '' : imgSrc); // Clear the preview if clearAll is true, otherwise show the original image
            fileInputRef.current.value = '';  // Clear the input field
        }
    }));

    useEffect(() => {
        if (!sasToken) return;

        // Only act if this uploader instance requested the token to avoid firing multiple uploads
        if (pendingSASToken.current && sasToken !== lastUsedToken.current) { // New token received
            pendingSASToken.current = false;
            lastUsedToken.current = sasToken;

            ref.current?.uploadImage();
        }
    }, [sasToken]);

    // Keep preview in sync with latest saved image URL
    useEffect(() => {
        if (imgSrc && imgSrc !== previewUrl) {
            setPreviewUrl(imgSrc);
        }
    }, [imgSrc]);

    const handleImageChange = event => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            onImageSelect(file);
        }
    };

    return (
        <div className='flex  gap-3'>
            <AvatarLogo src={previewUrl} size="lg" displayType={displayType} />
            <input type="file" accept={allowedTypes.join(',')} onChange={handleImageChange} ref={fileInputRef} style={{ display: 'none' }} />
            <div className="flex items-center">
                <Button color="blue" className="h-auto flex items-center gap-3" size="sm" onClick={() => fileInputRef.current.click()}>
                    <FaImage size={20} /> Choose Image
                </Button>   
            </div>

            <AlertManager 
                successMessage={''}
                setSuccessMessage={() => {}}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
            />

            {isUploading && <p>Uploading... {uploadProgress.toFixed(2)}%</p>}
        </div>
    );
});
ImageUploader.propTypes = {
    imgSrc: PropTypes.string,
    onSavePath: PropTypes.func,
    imageType: PropTypes.string,
    imageId: PropTypes.number,
    onImageSelect: PropTypes.func,
    displayType: PropTypes.string
};

export default ImageUploader;
